import { useState } from 'react';
import type { Action, OnboardingState } from './state.js';

type Plan = OnboardingState['plan'];

const PLANS: {
  id: Plan;
  name: string;
  price: string;
  monthly: string | null;
  features: string[];
}[] = [
  {
    id: 'tablette',
    name: 'Tablette seule',
    price: '249 €',
    monthly: null,
    features: ['App famille', 'Agenda, messages, photos', 'Mises à jour incluses'],
  },
  {
    id: 'famille',
    name: 'Famille',
    price: '249 €',
    monthly: '+ 10 €/mois',
    features: ['Tout de Tablette seule', 'Support prioritaire', 'Sauvegardes chiffrées'],
  },
  {
    id: 'presence',
    name: 'Présence plus',
    price: '249 €',
    monthly: '+ 30 €/mois',
    features: ['Tout de Famille', 'SIM 4G intégrée', 'Sans Wi-Fi requis'],
  },
];

interface Props {
  state: OnboardingState;
  dispatch: React.Dispatch<Action>;
  onNext: () => void;
  onBack: () => void;
}

export function Step6Formule({ state, dispatch, onNext }: Props) {
  const [plan, setPlan] = useState<Plan>(state.plan);
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  const canConfirm =
    cardName.trim() &&
    cardNumber.replace(/\s/g, '').length >= 16 &&
    expiry.length >= 4 &&
    cvc.length >= 3;

  function handleConfirm() {
    dispatch({ type: 'SET_PLAN', plan });
    dispatch({ type: 'COMPLETE' });
    onNext();
  }

  // PLANS contient toujours les 3 formules fixes — le find ne peut pas retourner undefined
  const selectedPlan = PLANS.find((p) => p.id === plan);
  const selected = selectedPlan ?? PLANS[1];
  if (!selected) return null;

  return (
    <div
      className="container"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}
    >
      <div style={{ position: 'sticky', top: '160px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>
          Étape 5 — Formule
        </div>
        <h1
          className="display"
          style={{ fontSize: 'clamp(42px, 4.5vw, 64px)', margin: '0 0 20px' }}
        >
          Choisissez votre <em>formule</em>.
        </h1>
        <p
          className="serif"
          style={{
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: 1.4,
            color: 'var(--ink-soft)',
          }}
        >
          Sans engagement. Résiliable à tout moment.
        </p>
      </div>

      <div className="card">
        {/* Plan selector */}
        <div style={{ display: 'grid', gap: '10px', marginBottom: '28px' }}>
          {PLANS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlan(p.id)}
              style={{
                padding: '16px 18px',
                borderRadius: '10px',
                border: `2px solid ${plan === p.id ? 'var(--terracotta)' : 'var(--hairline)'}`,
                background: plan === p.id ? 'oklch(0.97 0.016 50)' : 'var(--paper)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: 'Fraunces Variable, serif',
                    fontWeight: 500,
                    fontSize: '17px',
                    color: 'var(--ink)',
                    marginBottom: '4px',
                  }}
                >
                  {p.name}
                </div>
                <div className="small">{p.features.join(' · ')}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontFamily: 'Fraunces Variable, serif',
                    fontWeight: 500,
                    fontSize: '18px',
                    color: 'var(--ink)',
                  }}
                >
                  {p.price}
                </div>
                {p.monthly && <div className="small">{p.monthly}</div>}
              </div>
            </button>
          ))}
        </div>

        {/* Payment */}
        <div
          style={{
            borderTop: '1px solid var(--hairline-soft)',
            paddingTop: '24px',
            marginBottom: '24px',
          }}
        >
          <div className="eyebrow" style={{ marginBottom: '16px' }}>
            Paiement sécurisé
          </div>
          <div className="field">
            <label htmlFor="card-name">Nom sur la carte</label>
            <input
              id="card-name"
              type="text"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              placeholder="Jean Dupont"
              autoComplete="cc-name"
            />
          </div>
          <div className="field">
            <label htmlFor="card-number">Numéro de carte</label>
            <input
              id="card-number"
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/[^\d\s]/g, '').slice(0, 19))}
              placeholder="1234 5678 9012 3456"
              autoComplete="cc-number"
              style={{ fontFamily: 'JetBrains Mono Variable, monospace', letterSpacing: '0.06em' }}
            />
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="expiry">Expiration</label>
              <input
                id="expiry"
                type="text"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value.slice(0, 5))}
                placeholder="MM/AA"
                autoComplete="cc-exp"
              />
            </div>
            <div className="field">
              <label htmlFor="cvc">CVC</label>
              <input
                id="cvc"
                type="text"
                value={cvc}
                onChange={(e) => setCvc(e.target.value.slice(0, 4))}
                placeholder="123"
                autoComplete="cc-csc"
              />
            </div>
          </div>
          <p className="small" style={{ margin: '4px 0 0', color: 'var(--ink-muted)' }}>
            Paiement traité par Stripe ·{' '}
            {selected.monthly
              ? `${selected.price} aujourd'hui, puis ${selected.monthly}`
              : `${selected.price} · paiement unique`}
            {plan === 'presence' ? ' · TVA 20 % incluse' : ''}
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-lg"
          style={{ width: '100%' }}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          Confirmer la commande
        </button>
      </div>
    </div>
  );
}
